const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

/** Coordinates use the layout viewport, including visual viewport offsets. */
export function placeGuidedTour(target, popup, viewport, gap = 12) {
  const x = viewport.left || 0, y = viewport.top || 0;
  const margin = 12;
  const width = Math.max(0, Math.min(360, viewport.width - margin * 2));
  const above = target ? Math.max(0, target.top - y - gap - margin) : 0;
  const below = target ? Math.max(0, y + viewport.height - target.bottom - gap - margin) : 0;
  const side = !target ? 'center' : below >= popup.height || below >= above ? 'bottom' : 'top';
  const maxHeight = Math.max(0, Math.min(viewport.height - margin * 2,
    target && Math.max(above, below) >= 160 ? (side === 'bottom' ? below : above) : viewport.height - margin * 2));
  const height = Math.min(popup.height, maxHeight);
  return {
    left: clamp(target ? target.left + target.width / 2 - width / 2 : x + (viewport.width - width) / 2, x + margin, x + viewport.width - width - margin),
    top: clamp(side === 'bottom' ? target.bottom + gap : side === 'top' ? target.top - gap - height : y + (viewport.height - height) / 2, y + margin, y + viewport.height - margin - height),
    width, maxHeight, side,
  };
}

let nextId = 0;
/** No step actions are invoked. onClose receives complete, skip, escape, or close. */
export function createGuidedTour({ document: doc = globalThis.document, window: win = globalThis.window, onClose = () => {}, onStep = () => {}, labels = {} } = {}) {
  const copy = { back: 'Назад', next: 'Далее', skip: 'Позже', done: 'Завершить', ...labels };
  const focusSelector = 'a[href],button,input,select,textarea,[tabindex]';
  let root, popup, outline, title, body, count, back, forward, target, previousFocus, copyPanel, progress;
  let scroller, previousScroll = 0;
  const reduced = () => win.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? true;
  let steps = [], index = 0, frame = null, observer;
  const listeners = [];
  const listen = (node, type, fn, options) => {
    node.addEventListener(type, fn, options);
    listeners.push(() => node.removeEventListener(type, fn, options));
  };
  function element(tag, className, parent, text) {
    const node = doc.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    parent.appendChild(node);
    return node;
  }
  function resolve(step) {
    try {
      const node = doc.querySelector(step.selector);
      return node?.getClientRects().length ? node : null;
    } catch { return null; }
  }
  function layout() {
    if (!root) return;
    target = resolve(steps[index]);
    const rect = target?.getBoundingClientRect();
    const vv = win.visualViewport;
    const viewport = { left: vv?.offsetLeft || 0, top: vv?.offsetTop || 0, width: vv?.width || win.innerWidth, height: vv?.height || win.innerHeight };
    popup.style.width = `${Math.max(0, Math.min(360, viewport.width - 24))}px`;
    popup.style.maxHeight = '';
    const visible = rect && rect.bottom > viewport.top && rect.top < viewport.top + viewport.height && rect.right > viewport.left && rect.left < viewport.left + viewport.width;
    const position = placeGuidedTour(visible ? rect : null, popup.getBoundingClientRect(), viewport);
    Object.assign(popup.style, { left: `${position.left}px`, top: `${position.top}px`, maxHeight: `${position.maxHeight}px` });
    popup.dataset.side = position.side;
    // A missing or offscreen target gets a centered explanation with no clickable hole.
    outline.hidden = !visible;
    root.classList.toggle('guided-tour--no-target', !visible);
    if (visible) Object.assign(outline.style, { left: `${rect.left - 4}px`, top: `${rect.top - 4}px`, width: `${rect.width + 8}px`, height: `${rect.height + 8}px` });
  }
  function schedule() {
    if (frame === null) frame = win.requestAnimationFrame(() => { frame = null; layout(); });
  }
  function allowed(node) { return popup?.contains(node) || (steps[index]?.interactive !== false && target?.contains(node)); }
  function guard(event) {
    if (!allowed(event.target)) { event.preventDefault(); event.stopImmediatePropagation(); }
  }
  function focusables() {
    return [...(target && steps[index]?.interactive !== false ? [target, ...target.querySelectorAll(focusSelector)] : []), ...popup.querySelectorAll(focusSelector)]
      .filter(node => node.tabIndex >= 0 && !node.disabled && !node.closest('[inert]') && node.getClientRects().length);
  }
  function keyboard(event) {
    if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); close('escape'); return; }
    if (event.key === 'Tab') {
      const nodes = focusables();
      const current = nodes.indexOf(doc.activeElement);
      const nextIndex = current < 0 ? (event.shiftKey ? nodes.length - 1 : 0) : (current + (event.shiftKey ? -1 : 1) + nodes.length) % nodes.length;
      event.preventDefault(); event.stopImmediatePropagation();
      (nodes[nextIndex] || popup).focus({ preventScroll: true });
      return;
    }
    if (!allowed(event.target)) { guard(event); return; }
    // Arrow keys on actual controls keep their native behavior.
    if (doc.activeElement === popup && !event.altKey && !event.ctrlKey && !event.metaKey) {
      if (event.key === 'ArrowRight') { event.preventDefault(); next(); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); prev(); }
    }
  }
  function show() {
    onStep(index);
    target = resolve(steps[index]);
    title.textContent = steps[index].title;
    body.textContent = steps[index].text;
    count.textContent = `${index + 1} / ${steps.length}`;
    progress.style.transform = `scaleX(${(index + 1) / steps.length})`;
    back.disabled = index === 0;
    forward.textContent = index === steps.length - 1 ? copy.done : copy.next;
    target?.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: reduced() ? 'instant' : 'smooth' });
    if (!reduced()) {
      copyPanel.getAnimations?.().forEach(animation => animation.cancel());
      copyPanel.animate?.([{opacity:0,transform:'translateY(6px)'},{opacity:1,transform:'translateY(0)'}],
        {duration:220,easing:'cubic-bezier(.16,1,.3,1)'});
    }
    observer?.disconnect();
    observer?.observe(popup);
    if (target) observer?.observe(target);
    layout();
    popup.focus({ preventScroll: true });
  }
  function close(reason = 'close') {
    if (!root) return;
    listeners.splice(0).forEach(remove => remove());
    observer?.disconnect(); observer = null;
    if (frame !== null) win.cancelAnimationFrame(frame);
    frame = null;
    const outgoing = root;
    outgoing.style.pointerEvents = 'none';
    const fade = !reduced() && outgoing.animate?.([{opacity:1},{opacity:0}], {duration:140,fill:'forwards'});
    if(fade) fade.finished.then(()=>outgoing.remove(),()=>outgoing.remove()); else outgoing.remove();
    root = null; target = null;
    scroller?.scrollTo?.({top:previousScroll,behavior:'instant'});
    if (previousFocus?.isConnected) previousFocus.focus?.({ preventScroll: true });
    previousFocus = null;
    onClose(reason);
  }
  function next() { if (root) { if (index === steps.length - 1) close('complete'); else { index++; show(); } } }
  function prev() { if (root && index > 0) { index--; show(); } }
  function open(items, startIndex = 0) {
    if (!Array.isArray(items) || items.some(step => !step || typeof step.selector !== 'string' || typeof step.title !== 'string' || typeof step.text !== 'string')) throw new TypeError('Tour steps require selector, title and text strings');
    if (root) close('close');
    if (!items.length) return;
    steps = items.map(step => ({ ...step })); index = clamp(Number(startIndex) || 0, 0, steps.length - 1);
    previousFocus = doc.activeElement;
    scroller = doc.getElementById?.('app-main'); previousScroll = scroller?.scrollTop || 0;
    root = element('div', 'guided-tour', doc.body);
    outline = element('div', 'guided-tour__spotlight', root);
    outline.setAttribute('aria-hidden', 'true');
    popup = element('section', 'guided-tour__popup', root);
    popup.tabIndex = -1;
    popup.setAttribute('role', 'dialog');
    if (steps.every(step => step.interactive === false)) popup.setAttribute('aria-modal', 'true');
    // Non-modal: the highlighted controls remain accessible and operable.
    const id = `guided-tour-${++nextId}`;
    popup.setAttribute('aria-labelledby', `${id}-title`);
    popup.setAttribute('aria-describedby', `${id}-text`);
    const meta = element('div', 'guided-tour__meta', popup);
    count = element('span', 'guided-tour__count', meta);
    count.setAttribute('aria-live', 'polite');
    const skip = element('button', 'guided-tour__skip', meta, copy.skip);
    copyPanel = element('div', 'guided-tour__copy', popup);
    title = element('h2', 'guided-tour__title', copyPanel); title.id = `${id}-title`;
    body = element('p', 'guided-tour__text', copyPanel); body.id = `${id}-text`;
    const track = element('div', 'guided-tour__track', popup);
    track.setAttribute('aria-hidden','true');
    progress = element('i', 'guided-tour__progress', track);
    const footer = element('div', 'guided-tour__footer', popup);
    const actions = element('div', 'guided-tour__actions', footer);
    back = element('button', 'guided-tour__back', actions, copy.back);
    forward = element('button', 'guided-tour__next', actions, copy.next);
    [skip, back, forward].forEach(node => { node.type = 'button'; });
    listen(skip, 'click', () => close('skip'));
    listen(back, 'click', prev); listen(forward, 'click', next);
    for (const type of ['pointerdown', 'mousedown', 'touchstart', 'click', 'dblclick', 'contextmenu']) listen(doc, type, guard, { capture: true, passive: false });
    listen(doc, 'keydown', keyboard, true);
    listen(doc, 'focusin', event => { if (!allowed(event.target)) popup.focus({ preventScroll: true }); }, true);
    listen(win, 'resize', schedule);
    listen(doc, 'scroll', schedule, true);
    if (win.visualViewport) { listen(win.visualViewport, 'resize', schedule); listen(win.visualViewport, 'scroll', schedule); }
    if (win.ResizeObserver) observer = new win.ResizeObserver(schedule);
    show();
  }
  return { open, close, next, prev };
}
